import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Select, Skeleton, Space, Statistic,
  Table, Tag, Tabs, Typography, message,
} from 'antd';
import { CodeField, DateField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection } from '../../../ui/forms';
import { PlusOutlined } from '@ant-design/icons';
import type {
  AssetCategory, AssetRow, CreateAssetRequest,
  TicketPriority, TicketRow, TicketStatus,
} from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Maintenance & assets.
 * Two tabs: Tickets (open work orders) + Assets (register with cert-expiry tracking).
 */
const PRIORITY_TONE: Record<TicketPriority, string> = {
  low: 'default', normal: 'processing', high: 'warning', critical: 'error',
};
const STATUS_TONE: Record<TicketStatus, string> = {
  open: 'warning', in_progress: 'processing', done: 'success', cancelled: 'default',
};
const CAT_LABELS: Record<AssetCategory, string> = {
  electrical: 'Electrical', plumbing: 'Plumbing', it: 'IT', furniture: 'Furniture',
  safety: 'Safety', vehicle: 'Vehicle', other: 'Other',
};

export function MaintenancePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [ticketOpen, setTicketOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);

  const tickets = useQuery({
    queryKey: ['tickets', institution],
    queryFn: () => productApi.listTickets(institution),
    enabled: !!institution,
  });
  const assets = useQuery({
    queryKey: ['assets', institution],
    queryFn: () => productApi.listAssets(institution),
    enabled: !!institution,
  });
  const updateTicket = useMutation({
    mutationFn: (v: { id: string; status: TicketStatus }) =>
      productApi.updateTicketStatus(institution, v.id, { status: v.status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tickets', institution] }); },
  });

  const today = new Date().toISOString().slice(0, 10);
  const soon = new Date(); soon.setUTCDate(soon.getUTCDate() + 30);
  const soonIso = soon.toISOString().slice(0, 10);
  const expiringCerts = (assets.data?.rows ?? []).filter(
    (a) => a.cert_valid_until && a.cert_valid_until >= today && a.cert_valid_until <= soonIso,
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Maintenance &amp; assets</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Every raised ticket lives here. Assets track warranty + fitness-certificate expiry.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Open tickets" value={tickets.data?.open ?? 0} valueStyle={{ color: (tickets.data?.open ?? 0) > 0 ? '#a35800' : undefined }} /></Card>
        <Card size="small"><Statistic title="All tickets" value={tickets.data?.total ?? 0} /></Card>
        <Card size="small"><Statistic title="Assets on file" value={assets.data?.total ?? 0} /></Card>
        <Card size="small"><Statistic title="Certs expiring soon" value={expiringCerts.length} valueStyle={{ color: expiringCerts.length > 0 ? '#a35800' : undefined }} /></Card>
      </div>

      {(tickets.data?.overdue_certs ?? 0) > 0 && (
        <Alert type="error" showIcon message={`${tickets.data?.overdue_certs} asset certificate${tickets.data!.overdue_certs === 1 ? '' : 's'} already expired.`} />
      )}

      <Tabs
        defaultActiveKey="tickets"
        items={[
          {
            key: 'tickets',
            label: 'Tickets',
            children: (
              <>
                <div style={{ marginBottom: 12, textAlign: 'right' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setTicketOpen(true)}>New ticket</Button>
                </div>
                {tickets.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
                {(tickets.data?.rows.length ?? 0) === 0 ? (
                  <Empty description={<span>No tickets yet. <a onClick={() => setTicketOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
                ) : (
                  <Table<TicketRow>
                    size="small"
                    pagination={{ pageSize: 15 }}
                    rowKey="id"
                    dataSource={tickets.data?.rows ?? []}
                    columns={[
                      { title: 'Subject', dataIndex: 'subject', key: 's', render: (v: string) => <b>{v}</b> },
                      { title: 'Asset', dataIndex: 'asset_name', key: 'a', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
                      { title: 'Reporter', dataIndex: 'reporter', key: 'r', width: 140 },
                      { title: 'Priority', dataIndex: 'priority', key: 'p', width: 100, render: (v: TicketPriority) => <Tag color={PRIORITY_TONE[v]}>{v}</Tag> },
                      { title: 'Opened', dataIndex: 'opened_at', key: 'o', width: 120, render: (v: string) => v.slice(0, 10) },
                      {
                        title: 'Status', dataIndex: 'status', key: 'st', width: 140,
                        render: (v: TicketStatus, r) => (
                          <Select
                            size="small" value={v} style={{ width: 130 }}
                            onChange={(next) => updateTicket.mutate({ id: r.id, status: next })}
                            options={(['open', 'in_progress', 'done', 'cancelled'] as TicketStatus[]).map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))}
                          />
                        ),
                      },
                    ]}
                  />
                )}
              </>
            ),
          },
          {
            key: 'assets',
            label: `Assets (${assets.data?.total ?? 0})`,
            children: (
              <>
                <div style={{ marginBottom: 12, textAlign: 'right' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssetOpen(true)}>Add asset</Button>
                </div>
                {(assets.data?.rows.length ?? 0) === 0 ? (
                  <Empty description={<span>No assets registered. <a onClick={() => setAssetOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
                ) : (
                  <Table<AssetRow>
                    size="small"
                    pagination={{ pageSize: 15 }}
                    rowKey="id"
                    dataSource={assets.data?.rows ?? []}
                    columns={[
                      { title: 'Code', dataIndex: 'code', key: 'c', width: 120, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                      { title: 'Name', dataIndex: 'name', key: 'n', render: (v: string) => <b>{v}</b> },
                      { title: 'Category', dataIndex: 'category', key: 'cat', width: 120, render: (v: AssetCategory) => <Tag>{CAT_LABELS[v]}</Tag> },
                      { title: 'Location', dataIndex: 'location', key: 'l', render: (v: string | null) => v ?? '—' },
                      {
                        title: 'Cert expiry', dataIndex: 'cert_valid_until', key: 'ce', width: 130,
                        render: (v: string | null) => {
                          if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
                          const today = new Date().toISOString().slice(0, 10);
                          const isOverdue = v < today;
                          const isSoon = !isOverdue && v <= soonIso;
                          return <Tag color={isOverdue ? 'error' : isSoon ? 'warning' : 'default'}>{v}</Tag>;
                        },
                      },
                      { title: 'Status', dataIndex: 'status', key: 's', width: 110, render: (v: string) => <Tag color={v === 'active' ? 'success' : v === 'retired' ? 'default' : 'warning'}>{v.replace(/_/g, ' ')}</Tag> },
                    ]}
                  />
                )}
              </>
            ),
          },
        ]}
      />

      <NewTicketModal open={ticketOpen} onClose={() => setTicketOpen(false)} institution={institution} assets={assets.data?.rows ?? []}
        onCreated={() => {
          setTicketOpen(false);
          void qc.invalidateQueries({ queryKey: ['tickets', institution] });
          void message.success('Ticket added');
        }}
      />
      <NewAssetModal open={assetOpen} onClose={() => setAssetOpen(false)} institution={institution}
        onCreated={() => {
          setAssetOpen(false);
          void qc.invalidateQueries({ queryKey: ['assets', institution] });
          void message.success('Asset added');
        }}
      />
    </Space>
  );
}

type TicketFormValues = { asset_id?: string; subject: string; reporter: string; priority: TicketPriority; note?: string };

function NewTicketModal({
  open, onClose, institution, assets, onCreated,
}: { open: boolean; onClose: () => void; institution: string; assets: AssetRow[]; onCreated: () => void }) {
  return (
    <FormModal<TicketFormValues>
      open={open} onClose={onClose}
      title="New ticket" submitText="Add"
      initial={{ priority: 'normal' }}
      mutation={(v) => productApi.createTicket(institution, {
        asset_id: v.asset_id || undefined,
        subject: v.subject,
        reporter: v.reporter,
        priority: v.priority,
        note: v.note?.trim() || undefined,
      })}
      onSuccess={onCreated}
    >
      <Field name="subject" label="Subject" required>
        <TextField placeholder="RO not filling" autoFocus />
      </Field>
      <FormSection cols={2}>
        <Field name="reporter" label="Reporter" required>
          <TextField placeholder="Ms Kaur" />
        </Field>
        <Field name="priority" label="Priority" required>
          <Select options={[
            { value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' },
            { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' },
          ]} />
        </Field>
      </FormSection>
      <Field name="asset_id" label="Asset" optional>
        <Select allowClear placeholder="Attach to an asset"
          options={assets.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))} />
      </Field>
      <Field name="note" label="Note">
        <Input.TextArea rows={3} />
      </Field>
    </FormModal>
  );
}

function NewAssetModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  return (
    <FormModal<CreateAssetRequest>
      open={open} onClose={onClose}
      title="Add asset" submitText="Add"
      initial={{ category: 'other' }}
      mutation={(v) => productApi.createAsset(institution, {
        code: v.code, name: v.name, category: v.category,
        location: v.location || undefined,
        warranty_until: v.warranty_until || undefined,
        cert_valid_until: v.cert_valid_until || undefined,
        note: v.note || undefined,
      })}
      onSuccess={onCreated}
    >
      <FormSection cols={2}>
        <Field name="code" label="Code" required>
          <CodeField placeholder="RO-01" autoFocus />
        </Field>
        <Field name="name" label="Name" required>
          <TextField placeholder="RO purifier — staff room" />
        </Field>
        <Field name="category" label="Category" required>
          <Select options={(Object.keys(CAT_LABELS) as AssetCategory[]).map((c) => ({ value: c, label: CAT_LABELS[c] }))} />
        </Field>
        <Field name="location" label="Location">
          <TextField placeholder="Staff room, ground floor" />
        </Field>
        <Field name="warranty_until" label="Warranty until">
          <DateField />
        </Field>
        <Field name="cert_valid_until" label="Certificate valid until">
          <DateField />
        </Field>
      </FormSection>
      <Field name="note" label="Note">
        <Input.TextArea rows={2} />
      </Field>
    </FormModal>
  );
}

